import { Navigate } from "react-router";
import { useFocusedNavigation } from "../../../hooks/useFocusedNavigation";
import { CheckoutProvider } from "./lib/CheckoutProvider";
import { RyftCardPaymentProvider } from "./lib/RyftCardPaymentProvider";
import { useCheckout } from "./lib/CheckoutProvider.shared";
import { EmptyBusinessBoundary } from "./lib/EmptyBusinessBoundary";
import { EmptySupplierBoundary } from "./lib/EmptySupplierBoundary";
import { EmptySupplierQueryParamBoundary } from "./lib/EmptySupplierQueryParamBoundary";
import { useBusinessUpdate } from "../../../hooks/api/v2/business/useBusinessUpdate";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";
import { useOrderUpdate } from "../../../hooks/api/v2/order/v2/useOrderUpdate";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import {
  ContextOrderBasketType,
  useBasketContext,
  useSellarUser,
} from "../../../hooks";
import { useEffect } from "react";
import type { CheckoutOrder, CheckoutOrderItem } from "./lib/types";
import { BusinessGetResponseType, OrderV2Dto } from "@sellar-io/api-v2/types";
import {
  CHECKOUT_EDIT_ORDER_BUYER,
  ORDER_BUYER,
  SUPPLIER,
} from "../../../constants/routes";
import { CheckoutPageLoading } from "./lib/CheckoutPageLoading";
import { ErrorMessage } from "../../../components/errorMessage/ErrorMessage";
import { CheckoutPageLayout } from "./lib/CheckoutPageLayout";
import { mapToInitialOrder } from "./lib/mapToInitialOrder";
import { mapToLegacyPaymentType } from "./lib/mapToLegacyPaymentType";
import { CheckoutOrderSummary } from "./lib/CheckoutOrderSummary";
import { Checkout } from "./lib/Checkout";
import { useCheckoutOrderSummary } from "./lib/useCheckoutOrderSummary";
import { ProcessOrderReturnType } from "./lib/usePlaceOrder";
import { isHttpError } from "~/utils/httpClient";
import {
  CheckoutAlerts,
  ErrorMessageAlert,
  StockLevelAlert,
  PricingChangeAlert,
  DistributionEducationAlert,
} from "./lib/CheckoutAlerts";
import { mapBasketOrderToCheckoutOrderItems } from "./lib/mapBasketOrderToCheckoutOrderItems";
import { useLocalStorage } from "usehooks-ts";
import { useStockList } from "../../../hooks/api/v2/stockList/useStockList";
import { useCheckoutPaymentOptions } from "../../../hooks/api/v2/checkout/useCheckoutPaymentOptions";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useValidateCheckout } from "./lib/useValidateCheckout";
import { usePlaceOrder } from "./lib/usePlaceOrder";
import { isPromotionalOrderItem } from "../categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { useStorefrontPromotionRewardItemSelectorParameters } from "../categoryPage/Storefront/StorefrontPromotion/StorefrontPromotionRewardItemSelector";
import { useLegalEntityCompanyCreditInfo } from "../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { useStorefrontBuyerChoiceDeliveryOption } from "../categoryPage/Storefront/StorefrontBuyerChoiceDeliveryOptionDialog/useStorefrontBuyerChoiceDeliveryOptions";

export const CheckoutV2EditOrderPage = () => {
  useFocusedNavigation(); // Hides all drawers - sidebar and basket drawer
  return (
    <CheckoutProvider>
      <EmptySupplierQueryParamBoundary>
        <EmptyBusinessBoundary>
          <EmptySupplierBoundary>
            <RyftCardPaymentProvider>
              <CheckoutV2EditOrderLayout />
            </RyftCardPaymentProvider>
          </EmptySupplierBoundary>
        </EmptyBusinessBoundary>
      </EmptySupplierQueryParamBoundary>
    </CheckoutProvider>
  );
};

export const CheckoutV2EditOrderLayout = () => {
  const orderId = useParamAsNumber(CHECKOUT_EDIT_ORDER_BUYER, "orderId");
  const { basketOrder: initialBasketOrder } = useGetBasketOrder({ orderId });
  const storefrontPromotionRewardItemSelectorParameters =
    useStorefrontPromotionRewardItemSelectorParameters();

  const {
    isLoading: isCheckoutLoading,
    business,
    supplier,
    error: checkoutPageError,
    connection,
    supplierUrlIdInParam,
    storefront,
    storefrontFulfillmentType,
    checkoutFulfillmentType,
    priceTierLink,
    paymentMethods,
    updateFulfillmentType,
    deleteDraftOrder,
  } = useCheckout();

  if (!business) throw new Error("Business object is missing"); // We're sure business exists because of <EmptyBusinessBoundary />
  if (!supplier) throw new Error("Supplier object is missing"); // We're sure supplier exists because of <EmptySupplierBoundary />

  const retailerId = business.Retailer?.id ?? 0;
  const businessUpdateMutation = useBusinessUpdate(business.id);
  const getOrderAsync = useOrder(orderId, {
    enabled: orderId > 0,
  });
  const isFetchingOrder = getOrderAsync.isLoading;
  const orderUpdateMutation = useOrderUpdate(orderId);
  const { user } = useSellarUser();
  const { user: firebaseUser } = useFirebaseUser();
  const canRequestCreditTerms = useFeatureFlagStore().isActive(
    "CHECKOUT_CREDIT_TERMS_REQUEST",
  );

  const userId = user?.id ?? 0;

  const sellarCreditBalanceQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: retailerId,
    },
    {
      enabled: !!retailerId,
    },
  );

  const canUserViewSellarCreditBalance =
    sellarCreditBalanceQuery.data?.userCanViewSellarCreditBalance ?? false;

  const sellarCreditBalance =
    sellarCreditBalanceQuery.data?.creditAmountAvailable ?? 0;

  const sellarCreditStatus =
    sellarCreditBalanceQuery.data?.status ?? "creditCheckNotPerformed";

  const isCompanyVerified =
    sellarCreditBalanceQuery.data?.isCompanyVerified ?? null;

  const creditProvider = sellarCreditBalanceQuery.data?.creditProvider ?? "two";

  const {
    loading: isPlacingOrder,
    error: placeOrderErrorMessage,
    mode: placeOrderMode,
    placeOrder,
    setError: setPlaceOrderErrorMessage,
  } = usePlaceOrder();
  const {
    handleValidateCheckout,
    loading: isValidatingCart,
    outOfStockProducts,
    pricingChangeProducts,
    activeProducts,
  } = useValidateCheckout();

  const supplierName = supplier.Business.name;
  const supplierId = supplier.id;
  const isPageLoading = isCheckoutLoading || isFetchingOrder || !firebaseUser;
  const pageLoadError = checkoutPageError || getOrderAsync.error;
  const existingOrder = getOrderAsync.data;

  const navTitle = `Checkout with ${supplierName}`;
  const supplierRelationshipPaymentDue =
    connection?.PaymentTermsRequest?.paymentDue ?? 0;
  const supplierRelationshipPaymentDueCalculateFrom =
    connection?.PaymentTermsRequest?.paymentDueCalculateFrom ??
    "invoiceIssueDate";
  const supplierRelationshipStatus = "pending";
  const existingOrderSupplierRetailerConnectionPaymentDue =
    existingOrder?.SupplierRetailerConnectionPaymentTermsRequest?.paymentDue ??
    supplierRelationshipPaymentDue;
  const existingOrderSupplierRetailerConnectionPaymentDueCalculateFrom =
    existingOrder?.SupplierRetailerConnectionPaymentTermsRequest
      ?.paymentDueCalculateFrom ?? supplierRelationshipPaymentDueCalculateFrom;
  const existingOrderSupplierRetailerConnectionStatus =
    existingOrder?.SupplierRetailerConnectionPaymentTermsRequest?.status ??
    supplierRelationshipStatus;

  // Existing order items come from the basket since users are editing their order
  const existingOrderItems = initialBasketOrder?.OrderItems ?? [];
  const existingProductIds = existingOrderItems.map(
    (orderItem) => orderItem.Product.id,
  );
  const orderSupplierId = existingOrder?.SupplierId ?? 0;
  const productList = useStockList(
    {
      id: existingProductIds,
      SupplierId: orderSupplierId,
    },
    {
      enabled: Boolean(existingProductIds.length > 0 && !!orderSupplierId),
    },
  );
  const {
    saveBuyersChoiceFulfillmentType,
    isValidBuyersChoiceFulfillmentType,
  } = useStorefrontBuyerChoiceDeliveryOption({ supplierId: supplier.id });

  const updatedProductListLookupMap = new Map(
    (productList.data?.pages[0] ?? []).map((item) => [item.id, item]),
  );

  const orderItems = existingOrderItems.map((item) => {
    const updatedProduct = updatedProductListLookupMap.get(item.ProductId);
    return {
      ...item,
      price: isPromotionalOrderItem(item)
        ? item.price
        : (updatedProduct?.ProductPriceTier?.price ?? item.price),
    };
  });

  const checkoutOrderItems = mapBasketOrderToCheckoutOrderItems(orderItems);

  const pricingChangeLookupMap = new Map(
    pricingChangeProducts.map((product) => [product.id, product.newPrice]),
  );

  const updatedBasketOrder: ContextOrderBasketType = {
    ...initialBasketOrder,
    OrderItems: orderItems.map((orderItem) => {
      const price = pricingChangeLookupMap.has(orderItem.Product.id)
        ? (pricingChangeLookupMap.get(orderItem.Product.id) ?? 0)
        : orderItem.price;
      return {
        ...orderItem,
        price,
      };
    }),
    vatPercentage: existingOrder?.vatPercentage,
    currency: existingOrder?.currency,
    RetailerId: existingOrder?.RetailerId ?? null,
    SupplierId: existingOrder?.SupplierId,
    initiator: "retailer",
  };

  const updatedCheckoutOrderItems = mapBasketOrderToCheckoutOrderItems(
    updatedBasketOrder.OrderItems ?? [],
  );

  const orderSummary = useCheckoutOrderSummary({
    supplierStorefront: storefront,
    order: updatedBasketOrder,
  });

  const initialOrderFulfillmentType =
    checkoutFulfillmentType ??
    storefrontFulfillmentType ??
    connection?.fulfillmentType;

  const { data: paymentOptions, error: paymentOptionsError } =
    useCheckoutPaymentOptions(
      {
        supplierId: supplier.id,
        orderTotal: orderSummary.totalCharge,
        orderId,
        orderItems: updatedCheckoutOrderItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        orderFulfillmentType:
          checkoutFulfillmentType ?? initialOrderFulfillmentType,
        canRequestCreditTerms,
      },
      {
        enabled: Boolean(supplier.id) && Boolean(retailerId) && Boolean(userId),
      },
    );

  const paymentOptionsErrorMessage = paymentOptionsError
    ? "Error fetching payment options for this order. Try again in a few minutes"
    : "";

  const availableCheckoutPaymentOptions = paymentOptions ?? [];

  const hasDraughtDutyOrderItems = updatedCheckoutOrderItems.some(
    (item) => item.qualifiesForDraughtDuty,
  );

  const handleUpdateOrder = async (
    params: CreateUpdatedOrderDataParams,
  ): Promise<ProcessOrderReturnType> => {
    const updatedOrderData = createUpdatedOrderData(params);
    try {
      // Update Delivery instructions on the business
      await businessUpdateMutation.mutateAsync({
        deliveryInstructions: params.order.deliveryInstructions,
      });

      // Update the order
      const { id } = await orderUpdateMutation.mutateAsync(updatedOrderData);

      // Delete order from draft immediately after updating the order
      // in the API
      deleteDraftOrder({
        initiator: "retailer",
        SupplierId: params.supplierId,
        RetailerId: params.business.Retailer?.id ?? 0,
      });

      // Post order update steps

      // Save buyers choice preferences
      if (
        isValidBuyersChoiceFulfillmentType(updatedOrderData.fulfillmentType)
      ) {
        saveBuyersChoiceFulfillmentType(updatedOrderData.fulfillmentType);
      }

      return { data: { id }, error: null };
    } catch (error) {
      const newError = isHttpError(error)
        ? error
        : new Error("Something went wrong");
      return {
        data: null,
        error: newError,
      };
    }
  };

  const goToSupplierStorefront = () => {
    window.location.replace(
      `${window.location.origin}${ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlIdInParam })}`,
    );
  };

  const handleProcessOrder = (order: CheckoutOrder) => {
    return handleUpdateOrder({
      order,
      vatPercentage: Number(orderSummary.tax.percentage),
      orders: checkoutOrderItems,
      business,
      supplierId: supplier.id,
    });
  };

  // Validate checkout when the page refreshes
  useEffect(() => {
    if (checkoutOrderItems.length === 0) return;

    // ensure sufficient stock levels

    handleValidateCheckout({
      supplierId,
      orderItemList: checkoutOrderItems,
      business,
      user: firebaseUser,
      storefrontLinkPassword: priceTierLink,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isPageLoading) {
    return (
      <CheckoutPageLoading description="Loading your order" title={navTitle} />
    );
  }

  if (pageLoadError) {
    return <ErrorMessage>{pageLoadError.message}</ErrorMessage>;
  }

  if (!existingOrder) {
    return (
      <Navigate
        to={SUPPLIER.href({ supplierIdOrUrlId: supplierUrlIdInParam })}
      />
    );
  }

  const initialOrder = mapToInitialOrder({
    order: {
      deliveryNotes: existingOrder.deliveryNotes ?? "",
      DeliveryAddress: existingOrder.DeliveryAddress,
      InvoiceAddress: existingOrder.InvoiceAddress,
      purchaseOrderNumber: existingOrder.purchaseOrderNumber ?? "",
      retailerContactSmsNumber: existingOrder.retailerContactSmsNumber ?? "",
      paymentTermsRequest: {
        paymentDue: existingOrderSupplierRetailerConnectionPaymentDue,
        paymentDueCalculateFrom:
          existingOrderSupplierRetailerConnectionPaymentDueCalculateFrom,
        status: existingOrderSupplierRetailerConnectionStatus,
      },
      hasNoDraughtDutyRelief: existingOrder.hasNoDraughtDutyRelief,
      distributionContactId:
        existingOrder.OrderSellarDistributionMeta
          ?.SellarDistributionContactId ?? undefined,
      price: orderSummary.totalCharge,
    },
    connection,
    fulfillmentType: checkoutFulfillmentType ?? initialOrderFulfillmentType,
  });

  return (
    <>
      <CheckoutPageLayout
        title={navTitle}
        onClickLeftIcon={goToSupplierStorefront}
        layout="centered"
        aside={
          <CheckoutOrderSummary
            orderSummary={orderSummary}
            orderItemList={updatedCheckoutOrderItems}
            outOfStockProductIds={outOfStockProducts.map(
              (outOfStockProduct) => outOfStockProduct.id,
            )}
            productIdsWithPricingChange={pricingChangeProducts.map(
              (pricingChangeProduct) => pricingChangeProduct.id,
            )}
            activeProductIdList={activeProducts.map(
              (activeProduct) => activeProduct.id,
            )}
            promotions={
              storefrontPromotionRewardItemSelectorParameters.promotions
            }
          />
        }
        alerts={
          <CheckoutAlerts
            alerts={[
              <ErrorMessageAlert
                key="error-message-alert"
                message={placeOrderErrorMessage || paymentOptionsErrorMessage}
              />,
              <StockLevelAlert
                key="out-of-stock-alert"
                outOfStockProducts={outOfStockProducts}
                supplierName={supplierName}
                onClick={goToSupplierStorefront}
              />,
              <PricingChangeAlert
                key="pricing-change-alert"
                pricingChangeProducts={pricingChangeProducts}
                oldCheckoutOrderItems={checkoutOrderItems}
                newCheckoutOrderItems={updatedCheckoutOrderItems}
              />,
              <DistributionEducationAlert
                key="sellar-distribution-education-alert"
                supplierName={supplierName}
                fulfillmentType={initialOrder.deliveryType}
              />,
            ]}
          />
        }
      >
        <Checkout
          key={availableCheckoutPaymentOptions.join(",")} //This ensures that we have an up to date list of available payment types at all times
          title={navTitle}
          basketOrder={updatedBasketOrder}
          orderSummary={orderSummary}
          hasDraughtDutyOrderItems={hasDraughtDutyOrderItems}
          sellarCreditBalance={sellarCreditBalance}
          isCompanyVerified={isCompanyVerified}
          creditProvider={creditProvider}
          canUserViewSellarCreditBalance={canUserViewSellarCreditBalance}
          isOrderValueGreaterThanCreditBalance={
            orderSummary.totalCharge > sellarCreditBalance
          }
          placeOrderMode={placeOrderMode}
          sellarCreditStatus={sellarCreditStatus}
          isValidatingCart={isValidatingCart}
          availablePaymentTypes={availableCheckoutPaymentOptions}
          initialCheckoutOrder={initialOrder}
          hasOutOfStockItems={outOfStockProducts.length > 0}
          business={business}
          supplier={supplier}
          isPlacingOrder={isPlacingOrder}
          checkoutProviderProps={{
            connection,
            priceTierLink,
            storefront,
            paymentMethods,
            checkoutFulfillmentType,
            updateFulfillmentType,
          }}
          // called when changing collapsible checkout card

          onValidateCheckout={async () => {
            // ensure sufficient stock levels

            await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });
          }}
          onUpdateInvoiceEmails={async (emails: string[]) => {
            await businessUpdateMutation.mutateAsync({
              invoiceEmails: emails,
            });
          }}
          onPlaceOrderWithStripe={async ({ order, stripe, elements }) => {
            if (!stripe || !elements) {
              setPlaceOrderErrorMessage(
                "Something went wrong while trying to process this order",
              );
              return;
            }

            // ensure sufficient stock levels

            const validationResponse = await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });

            const isValid = validationResponse.outOfStockProducts.length === 0;

            if (!isValid) return;

            if (order.payment.type === "new-card-payment") {
              await placeOrder({
                type: "place-order-with-new-card",
                stripeElements: elements,
                stripePaymentMethodId: undefined,
                stripe,
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order),
              });
              return;
            }

            if (order.payment.type === "existing-card-payment") {
              await placeOrder({
                type: "place-order-with-existing-card",
                stripePaymentMethodId: order.payment.paymentMethodId ?? "",
                stripeElements: undefined,
                stripe,
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order),
              });
              return;
            }
          }}
          onPlaceOrderWithoutStripe={async ({ order }) => {
            // ensures sufficient stocks levels

            const validationResponse = await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });

            const isValid = validationResponse.outOfStockProducts.length === 0;

            if (!isValid) return;

            if (
              order.payment.type === "request-proforma-invoice" ||
              order.payment.type === "credit-account" ||
              order.payment.type === "pay-on-delivery" ||
              order.payment.type === "request-credit-terms"
            ) {
              await placeOrder({
                type: "place-order-without-card",
                supplierUrlIdInParam,
                processOrder: () => handleProcessOrder(order),
              });
              return;
            }

            if (order.payment.type === "sellar-credit") {
              await placeOrder({
                type: "place-order-with-sellar-credit",
                processOrder: () => handleProcessOrder(order),
                supplierUrlIdInParam,
              });
              return;
            }
          }}
          onPlaceOrderWithRyft={async ({ order }) => {
            const validationResponse = await handleValidateCheckout({
              user: firebaseUser,
              business,
              orderItemList: checkoutOrderItems,
              supplierId,
              storefrontLinkPassword: priceTierLink,
            });

            const isValid = validationResponse.outOfStockProducts.length === 0;

            if (!isValid) {
              return;
            }

            await placeOrder({
              type: "place-order-with-new-ryft-card",
              supplierUrlIdInParam,
              processOrder: () => handleProcessOrder(order),
            });
            return;
          }}
        />
      </CheckoutPageLayout>
      {isPlacingOrder && (
        <div className="absolute inset-0 z-50">
          <CheckoutPageLoading
            description="Placing your order"
            title={navTitle}
          />
        </div>
      )}
    </>
  );
};

type CreateUpdatedOrderDataParams = {
  order: CheckoutOrder;
  vatPercentage: number;
  orders: CheckoutOrderItem[];
  business: BusinessGetResponseType;
  supplierId: number;
};
const createUpdatedOrderData = ({
  order,
  orders,
  business,
  vatPercentage,
  supplierId,
}: CreateUpdatedOrderDataParams): OrderV2Dto.OrderUpdateRequestType => {
  if (!order.payment.type) throw new Error("Payment type not set");
  const paymentType = order.payment.type;
  const orderItems = orders.map((orderItem) => ({
    ProductId: Number(orderItem.productId),
    quantity: orderItem.quantity,
    PromotionRewardItemId: orderItem.promotionRewardItemId ?? undefined,
  }));
  const hasNoDraughtDutyRelief = order.draughtDuty === "yes";
  const paymentTermsRequest =
    order.payment.type === "request-credit-terms"
      ? order.paymentTermsRequest
      : undefined;
  const legacyPaymentType = mapToLegacyPaymentType({
    paymentType,
    invoiceMethod: order.invoiceMethod,
  });

  return {
    requestingBusinessType: "retailer",
    paymentType: legacyPaymentType,
    InvoiceAddressId: business.InvoiceAddressId,
    DeliveryAddressId: business.DeliveryAddressId,
    DeliveryAddress: order.deliveryAddress,
    InvoiceAddress: order.invoiceAddress,
    vatPercentage,
    currency: "gbp",
    SupplierId: supplierId,
    purchaseOrderNumber: order.purchaseOrderNumber,
    price: order.price,
    fulfillmentType: order.deliveryType,
    OrderItems: orderItems,
    orderComment: order.comment,
    retailerContactSmsNumber: order.phoneNumber || null,
    mailingListPermission: order.mailingListPermission,
    hasNoDraughtDutyRelief,
    paymentTermsRequest,
    distributionContactId: order.distributionContactId,
    RetailerSellarDistributionMeta: {
      timePreference: order.timePreference ?? null,
    },
    paymentLinkRequested:
      legacyPaymentType === "manual" &&
      order.paymentLinkRequested &&
      order.price > 0,
  };
};

// We want to persist the basket order in local storage between page refreshes
const useGetBasketOrder = ({ orderId }: { orderId: number }) => {
  const [{ order: initialBasketOrder }, dispatchBasket] = useBasketContext();
  const [basketOrder, setBasketOrder] =
    useLocalStorage<ContextOrderBasketType | null>(
      `CHECKOUT-#${orderId}`,
      null,
    );

  // Sync basket context to localStorage
  useEffect(() => {
    if (!initialBasketOrder || !initialBasketOrder.OrderItems) return;
    setBasketOrder(initialBasketOrder);
  }, [setBasketOrder, initialBasketOrder]);

  return { basketOrder, dispatchBasket };
};
